import React, { useEffect, useRef, useState } from "react";
import { FaSearch } from "react-icons/fa";

interface VendorSourceOption {
  vendor_source_id: number;
  vendor_source_name: string;
}

interface VendorSourceMultiSelectProps {
  options: VendorSourceOption[];
  selectedIds: number[];
  onChange: (selectedIds: number[]) => void;
  disabled: boolean;
  getLabel: (option: VendorSourceOption) => string;
}

const VendorSourceMultiSelect: React.FC<VendorSourceMultiSelectProps> = ({
  options,
  selectedIds,
  onChange,
  disabled,
  getLabel,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const visibleOptions = options.filter((option) =>
    getLabel(option).toLowerCase().includes(search.toLowerCase())
  );

  const toggleOption = (vendorSourceId: number) => {
    onChange(
      selectedIds.includes(vendorSourceId)
        ? selectedIds.filter((id) => id !== vendorSourceId)
        : [...selectedIds, vendorSourceId]
    );
  };

  const allVisibleSelected = visibleOptions.length > 0 && visibleOptions.every((option) =>
    selectedIds.includes(option.vendor_source_id)
  );

  const toggleVisibleOptions = () => {
    const visibleIds = visibleOptions.map((option) => option.vendor_source_id);
    onChange(
      allVisibleSelected
        ? selectedIds.filter((id) => !visibleIds.includes(id))
        : Array.from(new Set([...selectedIds, ...visibleIds]))
    );
  };

  const selectedLabels = options
    .filter((option) => selectedIds.includes(option.vendor_source_id))
    .map(getLabel);

  return (
    <div ref={wrapperRef} className="relative w-full">
      <button
        type="button"
        onClick={() => !disabled && setIsOpen((previous) => !previous)}
        disabled={disabled}
        aria-haspopup="listbox"
        className={`flex min-h-[42px] w-full flex-wrap items-center gap-1 rounded-lg border p-2 text-left text-sm shadow-inner ${
          disabled ? "cursor-not-allowed bg-gray-100 text-gray-800" : "bg-white text-black"
        }`}
      >
        {selectedLabels.length ? (
          selectedLabels.map((label) => (
            <span key={label} className="rounded-md bg-blue-100 px-2 py-1 text-xs text-blue-700">
              {label}
            </span>
          ))
        ) : (
          <span className="text-gray-400">Select vendor sources</span>
        )}
      </button>

      {isOpen && !disabled && (
        <div className="absolute z-50 mt-1 max-h-72 w-full overflow-y-auto rounded-lg border bg-white p-3 text-sm shadow-lg">
          <div className="mb-2 flex items-center rounded-md border border-gray-300 bg-gray-50 px-2 py-1">
            <FaSearch className="mr-2 text-gray-400" size={14} />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search..."
              className="w-full bg-transparent outline-none"
            />
          </div>
          <label className="flex cursor-pointer items-center rounded-md p-2 hover:bg-indigo-50">
            <input type="checkbox" checked={allVisibleSelected} onChange={toggleVisibleOptions} className="mr-2" />
            Select All
          </label>
          {visibleOptions.map((option) => (
            <label key={option.vendor_source_id} className="flex cursor-pointer items-center rounded-md p-2 hover:bg-indigo-50">
              <input
                type="checkbox"
                checked={selectedIds.includes(option.vendor_source_id)}
                onChange={() => toggleOption(option.vendor_source_id)}
                className="mr-2"
              />
              {getLabel(option)}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

export default VendorSourceMultiSelect;
